import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { TrendUp, Wallet, ChartPie, SignOut, CaretDown, Plus, Trophy, UserCircle, Sun, Moon, Bell, ShieldCheck } from '@phosphor-icons/react';
import { toast } from 'sonner';
import BrandLogo from '@/components/BrandLogo';

export default function Header() {
  const { user, logout, api, refreshUser } = useAuth();
  const { isDark, toggleTheme } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [showNotifs, setShowNotifs] = useState(false);

  const fetchNotifs = useCallback(async () => {
    try {
      const [countRes, notifsRes] = await Promise.all([
        api.get('/notifications/unread-count'),
        api.get('/notifications')
      ]);
      setUnreadCount(countRes.data.count);
      setNotifications(notifsRes.data.slice(0, 8));
    } catch {}
  }, [api]);

  useEffect(() => { fetchNotifs(); const iv = setInterval(fetchNotifs, 30000); return () => clearInterval(iv); }, [fetchNotifs]);

  const handleMarkAllRead = async () => {
    await api.post('/notifications/read-all');
    setUnreadCount(0);
    setNotifications(n => n.map(x => ({ ...x, read: true })));
  };

  const handleNotifClick = async (notif) => {
    if (!notif.read) {
      await api.post(`/notifications/${notif.id}/read`);
      setUnreadCount(c => Math.max(0, c - 1));
    }
    setShowNotifs(false);
    if (notif.market_id) navigate(`/market/${notif.market_id}`);
  };

  const handleLogout = async () => { await logout(); toast.success('Signed out'); };

  const navLinks = [
    { to: '/markets', label: 'Markets', icon: TrendUp },
    { to: '/portfolio', label: 'Portfolio', icon: ChartPie },
    { to: '/leaderboard', label: 'Leaderboard', icon: Trophy },
  ];

  return (
    <header className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl border-b border-[var(--oh-border)] h-16" style={{ backgroundColor: 'var(--oh-glass)' }} data-testid="main-header">
      <div className="max-w-7xl mx-auto h-full px-4 md:px-8 flex items-center justify-between">
        <div className="flex items-center gap-8">
          <Link to="/markets" data-testid="header-logo"><BrandLogo variant="header" /></Link>
          <nav className="hidden md:flex items-center gap-1">
            {navLinks.map(link => (
              <Link key={link.to} to={link.to} data-testid={`nav-${link.label.toLowerCase()}`}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  location.pathname === link.to ? 'text-[var(--oh-text)] bg-[var(--oh-bg-tertiary)]' : 'text-[var(--oh-text-secondary)] hover:text-[var(--oh-text)]'
                }`}>
                <link.icon size={16} weight={location.pathname === link.to ? 'fill' : 'regular'} /> {link.label}
              </Link>
            ))}
            {user?.role === 'admin' && (
              <Link to="/admin" data-testid="nav-admin"
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  location.pathname === '/admin' ? 'text-[#F59E0B] bg-[rgba(245,158,11,0.1)]' : 'text-[#F59E0B]/70 hover:text-[#F59E0B]'
                }`}>
                <ShieldCheck size={16} weight={location.pathname === '/admin' ? 'fill' : 'regular'} /> Admin
              </Link>
            )}
          </nav>
        </div>

        <div className="flex items-center gap-2">
          <button onClick={toggleTheme} data-testid="theme-toggle"
            className="w-9 h-9 rounded-lg flex items-center justify-center border border-[var(--oh-border)] hover:border-[var(--oh-border-hover)] transition-colors"
            style={{ backgroundColor: 'var(--oh-bg-secondary)' }}>
            {isDark ? <Sun size={16} className="text-[#F59E0B]" weight="fill" /> : <Moon size={16} className="text-[#6366f1]" weight="fill" />}
          </button>

          {/* Notifications Bell */}
          <div className="relative">
            <button onClick={() => setShowNotifs(!showNotifs)} data-testid="notification-bell"
              className="w-9 h-9 rounded-lg flex items-center justify-center border border-[var(--oh-border)] hover:border-[var(--oh-border-hover)] transition-colors relative"
              style={{ backgroundColor: 'var(--oh-bg-secondary)' }}>
              <Bell size={16} style={{ color: 'var(--oh-text-secondary)' }} weight={unreadCount > 0 ? 'fill' : 'regular'} />
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 w-4 h-4 bg-[#FF3B30] rounded-full text-[10px] text-white font-bold flex items-center justify-center" data-testid="unread-badge">
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </button>
            {showNotifs && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setShowNotifs(false)} />
                <div className="absolute right-0 top-12 w-80 rounded-xl border shadow-lg z-50 overflow-hidden" style={{ backgroundColor: 'var(--oh-card)', borderColor: 'var(--oh-border)' }} data-testid="notification-dropdown">
                  <div className="flex items-center justify-between px-4 py-3 border-b" style={{ borderColor: 'var(--oh-border)' }}>
                    <span className="text-sm font-bold" style={{ color: 'var(--oh-text)' }}>Notifications</span>
                    {unreadCount > 0 && (
                      <button onClick={handleMarkAllRead} className="text-xs text-[#00FFA3] hover:underline">Mark all read</button>
                    )}
                  </div>
                  <div className="max-h-72 overflow-y-auto">
                    {notifications.length === 0 ? (
                      <p className="text-sm text-center py-6" style={{ color: 'var(--oh-text-muted)' }}>No notifications</p>
                    ) : (
                      notifications.map(n => (
                        <button key={n.id} onClick={() => handleNotifClick(n)}
                          className={`w-full text-left px-4 py-3 border-b last:border-0 transition-colors hover:bg-[var(--oh-bg-tertiary)] ${!n.read ? 'bg-[rgba(0,255,163,0.03)]' : ''}`}
                          style={{ borderColor: 'var(--oh-border)' }}>
                          <div className="flex items-start gap-2">
                            {!n.read && <div className="w-2 h-2 rounded-full bg-[#00FFA3] mt-1.5 flex-shrink-0" />}
                            <div className="min-w-0">
                              <p className="text-xs font-bold" style={{ color: 'var(--oh-text)' }}>{n.title}</p>
                              <p className="text-xs truncate" style={{ color: 'var(--oh-text-muted)' }}>{n.message}</p>
                            </div>
                          </div>
                        </button>
                      ))
                    )}
                  </div>
                </div>
              </>
            )}
          </div>

          <Link to="/wallet" className="hidden sm:flex items-center gap-2 rounded-lg px-4 py-2 border border-[var(--oh-border)] hover:border-[var(--oh-border-hover)] transition-colors" style={{ backgroundColor: 'var(--oh-bg-secondary)' }} data-testid="wallet-link">
            <Wallet size={16} className="text-[#00FFA3]" weight="fill" />
            <span className="font-mono text-sm font-semibold" style={{ color: 'var(--oh-text)' }} data-testid="wallet-balance">
              ${(user?.wallet_balance || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </span>
          </Link>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="flex items-center gap-2 hover:bg-[var(--oh-bg-tertiary)] h-10 px-3" style={{ color: 'var(--oh-text)' }} data-testid="user-menu-trigger">
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#00FFA3] to-[#00B8FF] flex items-center justify-center text-black font-bold text-xs">
                  {(user?.name || 'U')[0].toUpperCase()}
                </div>
                <CaretDown size={14} className="text-[var(--oh-text-muted)]" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48" style={{ backgroundColor: 'var(--oh-card)', borderColor: 'var(--oh-border)', color: 'var(--oh-text)' }}>
              <div className="px-3 py-2 border-b" style={{ borderColor: 'var(--oh-border)' }}>
                <p className="text-sm font-medium truncate">{user?.name}</p>
                <p className="text-xs truncate" style={{ color: 'var(--oh-text-muted)' }}>{user?.email}</p>
              </div>
              <DropdownMenuItem className="cursor-pointer" asChild>
                <Link to={`/profile/${user?.id}`}><UserCircle size={16} className="mr-2" /> My Profile</Link>
              </DropdownMenuItem>
              <DropdownMenuItem className="cursor-pointer" asChild>
                <Link to="/wallet"><Wallet size={16} className="mr-2 text-[#00FFA3]" /> Wallet</Link>
              </DropdownMenuItem>
              <DropdownMenuItem className="cursor-pointer" asChild>
                <Link to="/portfolio"><ChartPie size={16} className="mr-2" /> Portfolio</Link>
              </DropdownMenuItem>
              {user?.role === 'admin' && (
                <DropdownMenuItem className="cursor-pointer" asChild>
                  <Link to="/admin"><ShieldCheck size={16} className="mr-2 text-[#F59E0B]" /> Admin Dashboard</Link>
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator style={{ backgroundColor: 'var(--oh-border)' }} />
              <DropdownMenuItem className="cursor-pointer text-[#FF3B30] focus:text-[#FF3B30]" onClick={handleLogout} data-testid="logout-button">
                <SignOut size={16} className="mr-2" /> Sign Out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  );
}
